import { useEffect, useState, useRef } from "react";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import api from "@/lib/api";
import { FIELD_TYPES, loadImage } from "@/lib/idcRender";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Upload, FileSpreadsheet, Save, Database, CalendarClock, Download, FileDown, Share2, Copy, Filter, ArrowUpAZ, ArrowDownAZ, Crop } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loading } from "@/components/Loading";
import PhotoCropDialog from "@/components/idc/PhotoCropDialog";
import BulkPhotoUpload from "@/components/idc/BulkPhotoUpload";

const uid = () => "f" + Math.random().toString(36).slice(2, 8);
const today = () => new Date().toISOString().slice(0, 10);

export default function DatasetsTab() {
  const [datasets, setDatasets] = useState([]);
  const [current, setCurrent] = useState(null);
  const [name, setName] = useState("");
  const [fields, setFields] = useState([]);
  const [rows, setRows] = useState([]);
  const [photoCache, setPhotoCache] = useState({});
  const [saving, setSaving] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportSel, setExportSel] = useState({});
  const excelRef = useRef(null);
  const photoInputs = useRef({});
  const [cropState, setCropState] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareInfo, setShareInfo] = useState({ enabled: false, token: "" });
  const [shareBusy, setShareBusy] = useState(false);
  const [filterField, setFilterField] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [sortDir, setSortDir] = useState(null);

  const loadList = () => api.get("/idc/datasets").then((r) => setDatasets(r.data)).catch(() => {}).finally(() => setListLoading(false));
  useEffect(() => { loadList(); }, []);

  const openNew = () => {
    setCurrent("new"); setName(""); setFields([]); setRows([]); setPhotoCache({}); setImportPreview(null);
    setShareInfo({ enabled: false, token: "" });
  };

  const openDataset = async (d) => {
    setImportPreview(null);
    setCurrent(d.id); setName(d.name); setFields(d.fields || []); setRows(d.rows || []);
    setShareInfo({ enabled: !!d.share_enabled, token: d.share_token || "" });
    const ids = [];
    (d.fields || []).filter((f) => f.type === "photo").forEach((f) => (d.rows || []).forEach((r) => { if (r[f.key]) ids.push(r[f.key]); }));
    if (ids.length) {
      const { data } = await api.post("/idc/photos/batch", { ids });
      setPhotoCache(data);
    } else setPhotoCache({});
  };

  const addField = () => setFields([...fields, { key: uid(), label: `Field ${fields.length + 1}`, type: "text" }]);
  const updateField = (i, patch) => setFields(fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  const removeField = (i) => {
    const key = fields[i].key;
    setFields(fields.filter((_, idx) => idx !== i));
    setRows(rows.map((r) => { const c = { ...r }; delete c[key]; return c; }));
  };

  const addRow = () => setRows([...rows, {}]);
  const updateCell = (ri, key, value) => setRows(rows.map((r, idx) => (idx === ri ? { ...r, [key]: value } : r)));
  const removeRow = (ri) => setRows(rows.filter((_, idx) => idx !== ri));

  const fillDate = (key) => setRows(rows.map((r) => (r[key] ? r : { ...r, [key]: today() })));

  const toggleShare = async (enabled) => {
    setShareBusy(true);
    try {
      const { data } = await api.post(`/idc/datasets/${current}/share`, { enabled });
      setShareInfo({ enabled: data.share_enabled, token: data.share_token });
      toast.success(enabled ? "Share link enabled" : "Share access turned off");
    } catch { toast.error("Failed"); } finally { setShareBusy(false); }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/#/share/${shareInfo.token}`).then(() => toast.success("Link copied"));
  };

  const viewRows = rows
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => {
      if (!filterField || !filterQuery.trim()) return true;
      return String(r[filterField] || "").toLowerCase().includes(filterQuery.trim().toLowerCase());
    })
    .sort((a, b) => {
      if (!filterField || !sortDir) return 0;
      const va = String(a.r[filterField] || "").toLowerCase();
      const vb = String(b.r[filterField] || "").toLowerCase();
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });

  const onPhoto = async (ri, key, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo cannot be larger than 5 MB");
    const reader = new FileReader();
    reader.onload = () => setCropState({ ri, key, src: reader.result });
    reader.readAsDataURL(file);
  };

  const onCropDone = async (dataUrl) => {
    const { ri, key } = cropState;
    setCropState(null);
    try {
      const { data } = await api.post("/idc/photos", { data: dataUrl });
      setPhotoCache((c) => ({ ...c, [data.id]: dataUrl }));
      updateCell(ri, key, data.id);
      toast.success("Photo added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Photo upload failed");
    }
  };

  const importExcel = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const wb = XLSX.read(reader.result, { type: "array", cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });
      if (!json.length) return toast.error("Excel file is empty");
      const headers = json[0].map((h) => String(h).trim());
      const mapping = headers.map((h, i) => ({ key: uid(), label: h || `Column ${i + 1}`, type: "text", col: i }));
      setImportPreview({ mapping, dataRows: json.slice(1).filter((r) => r.some((c) => c !== "")) });
      toast.info(`${mapping.length} columns found — set types and click Import`);
    };
    reader.readAsArrayBuffer(file);
  };

  const applyImport = () => {
    const { mapping, dataRows } = importPreview;
    const newFields = mapping.map((m) => ({ key: m.key, label: m.label, type: m.type }));
    const newRows = dataRows.map((r) => {
      const obj = {};
      mapping.forEach((m) => { obj[m.key] = m.type === "photo" ? "" : (r[m.col] != null ? String(r[m.col]) : ""); });
      return obj;
    });
    setFields(newFields);
    setRows(newRows);
    setImportPreview(null);
    toast.success(`${newRows.length} rows imported`);
  };

  const save = async () => {
    if (!fields.length) return toast.error("Create at least one field first");
    setSaving(true);
    try {
      const payload = { name, fields, rows };
      if (current === "new") {
        const { data } = await api.post("/idc/datasets", payload);
        setCurrent(data.id);
      } else {
        await api.put(`/idc/datasets/${current}`, payload);
      }
      toast.success("Data saved");
      loadList();
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const deleteDataset = async (id, dname) => {
    if (!window.confirm(`Delete "${dname}"?`)) return;
    await api.delete(`/idc/datasets/${id}`);
    if (current === id) setCurrent(null);
    loadList();
    toast.success("Deleted");
  };

  const selectedExportFields = () => fields.filter((f) => exportSel[f.key]);

  const exportExcel = async () => {
    const sel = selectedExportFields();
    if (!sel.length) return toast.error("Select at least one field");
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Data");
      ws.columns = [{ header: "No.", key: "no", width: 6 }, ...sel.map((f, i) => ({ header: f.label, key: "c" + i, width: f.type === "photo" ? 16 : 20 }))];
      ws.getRow(1).font = { bold: true };
      for (let vi = 0; vi < viewRows.length; vi++) {
        const { r } = viewRows[vi];
        const vals = { no: vi + 1 };
        sel.forEach((f, i) => { vals["c" + i] = f.type === "photo" ? "" : (r[f.key] || ""); });
        const row = ws.addRow(vals);
        let rowHasImg = false;
        sel.forEach((f, ci) => {
          if (f.type !== "photo") return;
          const dataUrl = r[f.key] && photoCache[r[f.key]];
          if (!dataUrl) return;
          const ext = dataUrl.startsWith("data:image/png") ? "png" : "jpeg";
          const imgId = wb.addImage({ base64: dataUrl.split(",")[1], extension: ext });
          ws.addImage(imgId, { tl: { col: ci + 1.05, row: row.number - 1 + 0.05 }, ext: { width: 52, height: 52 } });
          rowHasImg = true;
        });
        if (rowHasImg) row.height = 42;
      }
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${name || "data"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch {
      toast.error("Excel export failed");
    }
  };

  const exportPdf = () => {
    const sel = selectedExportFields();
    if (!sel.length) return toast.error("Select at least one field");
    const hasPhoto = sel.some((f) => f.type === "photo");
    const photoCols = {};
    sel.forEach((f, i) => { if (f.type === "photo") photoCols[i + 1] = f.key; });
    const doc = new jsPDF({ orientation: sel.length > 4 ? "landscape" : "portrait", unit: "mm", format: "a4" });
    doc.setFontSize(16);
    doc.text(name || "Data", 14, 15);
    autoTable(doc, {
      head: [["No.", ...sel.map((f) => f.label)]],
      body: viewRows.map(({ r }, vi) => [vi + 1, ...sel.map((f) => (f.type === "photo" ? "" : String(r[f.key] ?? "")))]),
      startY: 22,
      styles: { fontSize: 8, cellPadding: 1.5 },
      bodyStyles: hasPhoto ? { minCellHeight: 22, valign: "middle" } : {},
      headStyles: { fillColor: [8, 145, 178] },
      didDrawCell: (d) => {
        if (d.section !== "body") return;
        const pkey = photoCols[d.column.index];
        if (!pkey) return;
        const pid = viewRows[d.row.index]?.r?.[pkey];
        const img = pid && photoCache[pid];
        if (!img) return;
        try {
          const dim = Math.min(d.cell.width - 3, d.cell.height - 3);
          doc.addImage(img, "JPEG", d.cell.x + (d.cell.width - dim) / 2, d.cell.y + (d.cell.height - dim) / 2, dim, dim);
        } catch { /* skip */ }
      },
    });
    doc.save(`${name || "data"}.pdf`);
    toast.success("PDF downloaded");
  };

  if (current === null) {
    return (
      <div className="space-y-4" data-testid="datasets-list">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold">Your Data Files</h3>
          <Button onClick={openNew} data-testid="dataset-new-btn" className="gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus className="h-4 w-4" /> New Data
          </Button>
        </div>
        {listLoading ? (
          <Loading text="Loading data files..." />
        ) : datasets.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-10 text-center">
            <Database className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-muted-foreground text-sm">No data files yet. Create fields with "New Data" or upload Excel.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {datasets.map((d) => (
              <div key={d.id} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between hover:shadow-sm transition-shadow" data-testid={`dataset-card-${d.id.slice(0, 8)}`}>
                <button className="text-left flex-1" onClick={() => openDataset(d)}>
                  <p className="font-semibold text-sm">{d.name}</p>
                  <p className="text-xs text-muted-foreground">{(d.fields || []).length} fields · {(d.rows || []).length} rows</p>
                </button>
                <Button size="sm" variant="ghost" onClick={() => deleteDataset(d.id, d.name)} className="text-red-500 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dataset-editor">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => { setCurrent(null); loadList(); }}>← Back</Button>
        <Input placeholder="Data file name" value={name} onChange={(e) => setName(e.target.value)} data-testid="dataset-name-input" className="max-w-xs" />
        <div className="flex-1" />
        <input type="file" accept=".xlsx,.xls,.csv" ref={excelRef} className="hidden" onChange={(e) => { importExcel(e.target.files[0]); e.target.value = ""; }} />
        <Button variant="outline" onClick={() => excelRef.current?.click()} data-testid="excel-import-btn" className="gap-1.5">
          <FileSpreadsheet className="h-4 w-4" /> Excel Upload
        </Button>
        <BulkPhotoUpload
          photoFields={fields.filter((f) => f.type === "photo")}
          rowCount={rows.length}
          onUpload={async (dataUrl) => (await api.post("/idc/photos", { data: dataUrl })).data.id}
          onAssign={(ri, key, pid, dataUrl) => {
            setRows((rs) => rs.map((r, i) => (i === ri ? { ...r, [key]: pid } : r)));
            setPhotoCache((c) => ({ ...c, [pid]: dataUrl }));
          }}
          testId="bulk-photo"
        />
        <Button
          variant="outline"
          disabled={!fields.length}
          onClick={() => { setExportSel(Object.fromEntries(fields.map((f) => [f.key, true]))); setExportOpen(true); }}
          data-testid="data-export-btn"
          className="gap-1.5"
        >
          <Download className="h-4 w-4" /> Export
        </Button>
        <Button
          variant="outline"
          disabled={current === "new"}
          onClick={() => setShareOpen(true)}
          data-testid="share-link-btn"
          className="gap-1.5"
        >
          <Share2 className="h-4 w-4" /> Share
        </Button>
        <Button onClick={save} disabled={saving} data-testid="dataset-save-btn" className="gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white">
          <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      <div className="bg-card border border-border rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-heading font-semibold text-sm">Fields</p>
          <Button size="sm" variant="outline" onClick={addField} data-testid="add-field-btn" className="gap-1"><Plus className="h-3.5 w-3.5" /> Add Field</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {fields.map((f, i) => (
            <div key={f.key} className="flex items-center gap-1 border border-border rounded-lg p-1.5 bg-slate-50" data-testid={`field-chip-${i}`}>
              <Input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} className="h-7 w-28 text-xs" data-testid={`field-label-${i}`} />
              <Select value={f.type} onValueChange={(v) => updateField(i, { type: v })}>
                <SelectTrigger className="h-7 w-24 text-xs" data-testid={`field-type-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => removeField(i)} className="text-red-400 hover:text-red-600 px-1"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {fields.length === 0 && <p className="text-xs text-muted-foreground">Add a field or upload Excel.</p>}
        </div>
      </div>

      {importPreview && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 space-y-3" data-testid="column-mapping-panel">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-heading font-semibold text-sm">Column Mapping — choose a type for each column</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setImportPreview(null)}>Cancel</Button>
              <Button size="sm" onClick={applyImport} data-testid="column-mapping-apply-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">
                Import ({importPreview.dataRows.length} rows)
              </Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {importPreview.mapping.map((m, i) => (
              <div key={m.key} className="flex items-center gap-1.5 bg-white border border-border rounded-lg p-1.5">
                <Input
                  value={m.label}
                  onChange={(e) => setImportPreview({ ...importPreview, mapping: importPreview.mapping.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)) })}
                  className="h-7 text-xs"
                  data-testid={`mapping-label-${i}`}
                />
                <Select value={m.type} onValueChange={(v) => setImportPreview({ ...importPreview, mapping: importPreview.mapping.map((x, idx) => (idx === i ? { ...x, type: v } : x)) })}>
                  <SelectTrigger className="h-7 w-24 text-xs" data-testid={`mapping-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Note: data of "Photo / JPG" type columns is ignored — photos are uploaded manually per row.</p>
        </div>
      )}

      {fields.length > 0 && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <p className="font-heading font-semibold text-sm">Data ({viewRows.length === rows.length ? rows.length : `${viewRows.length}/${rows.length}`} rows)</p>
            <Button size="sm" variant="outline" onClick={addRow} data-testid="add-row-btn" className="gap-1"><Plus className="h-3.5 w-3.5" /> Add Row</Button>
          </div>
          <div className="px-4 py-2 border-b border-border flex flex-wrap items-center gap-2 bg-slate-50/60" data-testid="data-filter-bar">
            <Filter className="h-3.5 w-3.5 text-slate-400" />
            <Select value={filterField} onValueChange={(v) => { setFilterField(v); setSortDir(null); setFilterQuery(""); }}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="filter-field-select"><SelectValue placeholder="Select field" /></SelectTrigger>
              <SelectContent>{fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={filterQuery} onChange={(e) => setFilterQuery(e.target.value)} placeholder="Filter text..." disabled={!filterField} className="h-8 w-44 text-xs" data-testid="filter-query-input" />
            <Button size="sm" variant={sortDir === "asc" ? "default" : "outline"} disabled={!filterField} onClick={() => setSortDir(sortDir === "asc" ? null : "asc")} className="h-8 text-xs gap-1" data-testid="sort-az-btn">
              <ArrowUpAZ className="h-3.5 w-3.5" /> A → Z
            </Button>
            <Button size="sm" variant={sortDir === "desc" ? "default" : "outline"} disabled={!filterField} onClick={() => setSortDir(sortDir === "desc" ? null : "desc")} className="h-8 text-xs gap-1" data-testid="sort-za-btn">
              <ArrowDownAZ className="h-3.5 w-3.5" /> Z → A
            </Button>
            {(filterQuery || sortDir) && (
              <Button size="sm" variant="ghost" className="h-8 text-xs text-muted-foreground" onClick={() => { setFilterQuery(""); setSortDir(null); }} data-testid="filter-clear-btn">Clear</Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="data-table">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-2 py-2 text-xs text-slate-400 w-8">#</th>
                  {fields.map((f) => (
                    <th key={f.key} className="px-2 py-2 text-xs font-semibold text-slate-500 whitespace-nowrap">
                      {f.label}
                      {f.type === "date" && (
                        <button onClick={() => fillDate(f.key)} title="Aaj ki date bharein" className="ml-1 text-cyan-600 align-middle"><CalendarClock className="h-3.5 w-3.5 inline" /></button>
                      )}
                    </th>
                  ))}
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {viewRows.map(({ r: row, i: ri }, vi) => (
                  <tr key={ri} className="border-t border-border" data-testid={`data-row-${ri}`}>
                    <td className="px-2 py-1.5 text-xs text-slate-400">{vi + 1}</td>
                    {fields.map((f) => (
                      <td key={f.key} className="px-2 py-1.5">
                        {f.type === "photo" ? (
                          <div className="flex items-center gap-1.5">
                            {row[f.key] && photoCache[row[f.key]] && (
                              <img src={photoCache[row[f.key]]} alt="" className="h-8 w-8 rounded object-cover border cursor-pointer" title="Click to adjust photo" onClick={() => setCropState({ ri, key: f.key, src: photoCache[row[f.key]] })} data-testid={`photo-thumb-${ri}`} />
                            )}
                            <input type="file" accept="image/*" className="hidden" ref={(el) => (photoInputs.current[`${ri}-${f.key}`] = el)} onChange={(e) => { onPhoto(ri, f.key, e.target.files[0]); e.target.value = ""; }} />
                            {row[f.key] && photoCache[row[f.key]] && (
                              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setCropState({ ri, key: f.key, src: photoCache[row[f.key]] })} data-testid={`photo-adjust-${ri}`}>
                                <Crop className="h-3 w-3" /> Adjust
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => photoInputs.current[`${ri}-${f.key}`]?.click()} data-testid={`photo-upload-${ri}`}>
                              <Upload className="h-3 w-3" /> {row[f.key] ? "Change" : "Upload"}
                            </Button>
                          </div>
                        ) : (
                          <Input
                            type={f.type === "number" ? "number" : f.type === "email" ? "email" : f.type === "date" ? "date" : f.type === "time" ? "time" : "text"}
                            value={row[f.key] || ""}
                            onChange={(e) => updateCell(ri, f.key, e.target.value)}
                            className="h-7 text-xs min-w-28"
                            data-testid={`cell-${ri}-${f.key}`}
                          />
                        )}
                      </td>
                    ))}
                    <td className="px-2 py-1.5">
                      <button onClick={() => removeRow(ri)} className="text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                ))}
                {viewRows.length === 0 && (
                  <tr><td colSpan={fields.length + 2} className="px-4 py-6 text-center text-muted-foreground text-sm">{rows.length === 0 ? "Add a row or import from Excel." : "No rows match this filter."}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-sm" data-testid="export-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Export Data</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">Select fields to include — {rows.length} rows will be exported.</p>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {fields.map((f) => (
              <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`export-field-${f.key}`}>
                <Checkbox checked={!!exportSel[f.key]} onCheckedChange={(v) => setExportSel({ ...exportSel, [f.key]: v })} />
                {f.label} <span className="text-xs text-muted-foreground">({f.type})</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1 gap-1.5" onClick={exportExcel} data-testid="export-excel-btn">
              <FileSpreadsheet className="h-4 w-4" /> Excel
            </Button>
            <Button className="flex-1 gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white" onClick={exportPdf} data-testid="export-pdf-btn">
              <FileDown className="h-4 w-4" /> PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Share Data Entry Link</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Jisko ye link denge wo sirf data fill / edit / save kar payega — kuch delete ya settings change nahi kar payega. Aap kabhi bhi access band kar sakte hain.
          </p>
          {shareInfo.enabled && shareInfo.token ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 bg-slate-50 border border-border rounded-lg p-2">
                <p className="text-xs break-all flex-1 text-cyan-800" data-testid="share-link-text">{`${window.location.origin}/#/share/${shareInfo.token}`}</p>
                <Button size="sm" variant="outline" className="h-7 gap-1 text-xs shrink-0" onClick={copyShareLink} data-testid="share-copy-btn">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
              </div>
              <Button variant="outline" onClick={() => toggleShare(false)} disabled={shareBusy} data-testid="share-toggle-btn" className="w-full text-red-600 border-red-200 hover:bg-red-50">
                {shareBusy ? "..." : "Turn OFF Access"}
              </Button>
            </div>
          ) : (
            <Button onClick={() => toggleShare(true)} disabled={shareBusy} data-testid="share-toggle-btn" className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
              {shareBusy ? "..." : "Turn ON Access & Get Link"}
            </Button>
          )}
        </DialogContent>
      </Dialog>

      <PhotoCropDialog
        open={!!cropState}
        src={cropState?.src}
        onClose={() => setCropState(null)}
        onDone={onCropDone}
      />
    </div>
  );
}
